import { useMemo, useState } from 'react';
import { useAddToBasket } from '@/hooks/useAddToBasket';
import { PurchaseAction } from '@/constants';
import type { Product } from '@/types';
import type { GiftCardData } from './GiftCardAddToCart.types';
import * as Styled from './GiftCardAddToCart.styled';

interface GiftCardAddToCartProps {
  data: GiftCardData;
}

export const GiftCardAddToCart = ({ data }: GiftCardAddToCartProps) => {
  const [selectedCode, setSelectedCode] = useState<string | null>(
    data.products[0]?.code ?? null,
  );
  const [quantity, setQuantity] = useState(1);

  const selectedProduct = data.products.find(p => p.code === selectedCode);

  const product: Product = useMemo(
    () => ({
      author: null,
      format: null,
      image: { mainSrc: data.image, thumbnailSrc: data.image, altText: data.name },
      loyaltyPoints: 0,
      maxQuantity: data.maxQuantity || 100,
      name: data.name,
      price: { current: selectedProduct?.price ?? '', former: null },
      productType: null,
      productUrl: null,
      promoBadge: null,
      purchaseAction: PurchaseAction.CanPurchase,
      sku: selectedCode,
    }),
    [selectedCode, selectedProduct?.price, data.image, data.name, data.maxQuantity],
  );

  const { addToBasket, isPending } = useAddToBasket(product);

  const maxQuantity = data.maxQuantity || 100;
  const canSubmit = selectedCode !== null && quantity >= 1;

  const handleQuantityChange = (value: string) => {
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 1) {
      setQuantity(1);
    } else if (parsed > maxQuantity) {
      setQuantity(maxQuantity);
    } else {
      setQuantity(parsed);
    }
  };

  const handleSubmit = () => {
    if (!canSubmit || isPending) return;
    addToBasket({ code: selectedCode, quantity });
  };

  return (
    <Styled.FormWrapper>
      <Styled.Fieldset>
        <Styled.Legend>{data.amountLabel}</Styled.Legend>
        <Styled.AmountOptions>
          {data.products.map(product => (
            <Styled.AmountOption
              key={product.code}
              $isSelected={selectedCode === product.code}
            >
              <input
                type="radio"
                name="giftCardAmount"
                value={product.code}
                checked={selectedCode === product.code}
                onChange={() => setSelectedCode(product.code)}
              />
              {product.price}
            </Styled.AmountOption>
          ))}
        </Styled.AmountOptions>
      </Styled.Fieldset>

      <Styled.Fieldset>
        <Styled.Legend>{data.quantityLabel}</Styled.Legend>
        <Styled.QuantityStepper>
          <Styled.StepperButton
            type="button"
            disabled={quantity <= 1}
            onClick={() => setQuantity(q => Math.max(1, q - 1))}
            aria-label="Decrease quantity"
          >
            <svg width="9" height="2" viewBox="0 0 9 2" fill="none">
              <path
                d="M0 0.528H3.648H4.896H8.544V1.776H4.896H3.648H0V0.528Z"
                fill="currentColor"
              />
            </svg>
          </Styled.StepperButton>
          <Styled.QuantityInput
            type="number"
            value={quantity}
            min={1}
            max={maxQuantity}
            onChange={e => handleQuantityChange(e.target.value)}
            aria-label="Quantity"
          />
          <Styled.StepperButton
            type="button"
            disabled={quantity >= maxQuantity}
            onClick={() => setQuantity(q => Math.min(maxQuantity, q + 1))}
            aria-label="Increase quantity"
          >
            <svg width="9" height="10" viewBox="0 0 9 10" fill="none">
              <path
                d="M0 4.528H3.648V0.88H4.896V4.528H8.544V5.776H4.896V9.424H3.648V5.776H0V4.528Z"
                fill="currentColor"
              />
            </svg>
          </Styled.StepperButton>
        </Styled.QuantityStepper>
      </Styled.Fieldset>

      <Styled.AddToCartButton
        type="button"
        disabled={!canSubmit || isPending}
        aria-busy={isPending}
        onClick={handleSubmit}
      >
        {isPending ? 'Adding...' : 'Add to Cart'}
      </Styled.AddToCartButton>
    </Styled.FormWrapper>
  );
};
